import React, {useEffect, useState} from 'react';
import {Card, CardContent, Box, Link} from '@mui/material';
import BenefitItem from './BenefitItem';
import {
  ChevronDownDark,
  ChevronUpDark,
  LineSeparator,
} from '@jobseeker/lotus/assets/icons';
import {Image} from '@jobseeker/lotus/components/Image';
import {useInView, useIsCustomWidth} from '@jobseeker/lotus/hooks';
import {Benefits, JDQueryParams} from '../type';
import {getKeyFromPath, TrackEventUBA} from '@jobseeker/common-core/utils';
import dynamic from 'next/dynamic';
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const BottomSheet = dynamic(
  () => import('@jobseeker/lotus/elements/BottomSheet'),
  {ssr: false}
);

type empBen = {
  benefits: Benefits[];
  isAppIimjobs: boolean;
  templateInfo?: {
    templateNo?: string;
    origin?: string;
  };
  isExpanded?: boolean;
  onToggle?: () => void;
  heading?: string;
  jobInfo?: any;
  jdQueryParams?: JDQueryParams;
};
const EmployerBenefits: React.FC<empBen> = ({
  benefits,
  isAppIimjobs,
  templateInfo,
  isExpanded = false,
  onToggle,
  heading,
  jobInfo,
  jdQueryParams,
}) => {
  const [open, setOpen] = useState(false);

  const showAll = isExpanded;
  const toggleShowAll = onToggle || (() => {});

  const isSmall = useIsCustomWidth('sm');
  const [companyBenefitsTracked, setCompanyBenefitsTracked] = useState(false);

  const {ref, isInView} = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  useEffect(() => {
    if (isInView && !companyBenefitsTracked) {
      TrackEventUBA('sectionView', 'view', {
        ref: jdQueryParams?.ref || '',
        pageName: 'JD',
        jobID: jobInfo?.id,
        page: 'JD',
        templateID: templateInfo?.templateNo,
        isBranded: 'Yes',
        companyID: jobInfo?.companyData?.companyId,
        centralCompanyID: jobInfo?.recruiter?.centralizedCompanyId,
        companyName: jobInfo?.companyData?.companyName,
        section: 'Benefits',
        sessionUsername: sessionStorage.getItem('userId') || '',
      });
      setCompanyBenefitsTracked(true);
    }
  }, [isInView, companyBenefitsTracked]);
  const showBenefits = (show?: boolean) => {
    const displayedBenefits = showAll || show ? benefits : benefits.slice(0, 2);

    return (
      <Box ref={ref}>
        <Card
          variant="outlined"
          sx={{
            border: 'none',
            backgroundColor: open ? '#fff' : '#F9FAFB',
          }}
        >
          <CardContent
            sx={{
              padding: 2, // Add padding here instead
              '&:last-child': {
                pb: 2, // Also set bottom padding
              },
            }}
          >
            {displayedBenefits?.map((b, idx) => (
              <Box key={idx}>
                <BenefitItem
                  name={b.benefitsSubheading}
                  description={b.benefitsDescription}
                  isAppIimjobs={isAppIimjobs}
                />
                {idx !== displayedBenefits.length - 1 && (
                  <Box display="flex" justifyContent="center" my={1}>
                    <Image
                      src={LineSeparator}
                      alt="separator"
                      width={305}
                      style={{
                        minWidth: open ? 'calc(100% + 72px)' : '100%',
                        marginLeft: open ? -16 : 0,
                      }}
                      height={10}
                    />
                  </Box>
                )}
              </Box>
            ))}
            {benefits?.length > 2 && !show && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => {
                  TrackEventUBA('jdPageClick', 'click', {
                    ref: jdQueryParams?.ref || '',
                    pageName: 'Job Description Page',
                    jobID: jobInfo?.id,
                    jobVersion: '',
                    jobRef: getKeyFromPath('ref') || '',
                    jobPos: jdQueryParams?.jobPos || '',
                    page: 'JD',
                    section: 'Benefits',
                    isBranded: 'Yes',
                    sessionUsername: sessionStorage.getItem('userId') || '',
                    typeClick: `Benefits-${showAll ? 'ShowLess' : 'ShowMore'}`,
                    companyID: jobInfo?.companyData?.companyId,
                    centralCompanyID: jobInfo?.recruiter?.centralizedCompanyId,
                    companyName: jobInfo?.companyData?.companyName,
                    templateID: templateInfo?.templateNo,
                  });
                  if (isSmall) {
                    setOpen(true);
                  } else {
                    toggleShowAll();
                  }
                }}
                sx={{cursor: 'pointer', pt: 2}}
              >
                <Link
                  color="primary"
                  underline="none"
                  letterSpacing="0.48px"
                  lineHeight="18px"
                  fontSize={isAppIimjobs ? 12 : 14}
                  fontWeight={isAppIimjobs ? 500 : 600}
                >
                  {showAll ? 'Show Less' : 'Show More'}
                </Link>
                {!isSmall && (
                  <Image
                    src={!showAll ? ChevronDownDark : ChevronUpDark}
                    alt="chevron"
                    height={18}
                    width={18}
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };
  return (
    <>
      {' '}
      <BottomSheet
        maxHeight={600}
        open={open}
        title={heading || 'Company Verified Benefits'}
        onDismiss={() => {
          setOpen(false);
          TrackEventUBA('jdPageClick', 'click', {
            ref: jdQueryParams?.ref || '',
            pageName: 'Job Description Page',
            jobID: jobInfo?.id,
            jobVersion: '',
            jobRef: getKeyFromPath('ref') || '',
            jobPos: jdQueryParams?.jobPos || '',
            page: 'JD',
            section: 'Benefits',
            isBranded: 'Yes',
            sessionUsername: sessionStorage.getItem('userId') || '',
            typeClick: 'Benefits-ShowLess',
            companyID: jobInfo?.companyData?.companyId,
            centralCompanyID: jobInfo?.recruiter?.centralizedCompanyId,
            companyName: jobInfo?.companyData?.companyName,
            templateID: templateInfo?.templateNo,
          });
        }}
      >
        {showBenefits(true)}
      </BottomSheet>
      {showBenefits()}
    </>
  );
};

export default EmployerBenefits;
